import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Renders into the dark page header, next to the tab name.
export function HeaderSlot({ children }) {
  const [el, setEl] = useState(null);
  useEffect(() => { setEl(document.getElementById("page-header-slot")); }, []);
  return el ? createPortal(<div className="flex items-center gap-3">{children}</div>, el) : null;
}

// Searchable jump-to list. items: [{ id, label, meta }]
// variant "dark" = the small box in the black page header; "title" = the record's own heading is the trigger.
export function RecordSwitcher({ currentId, currentLabel, items, onPick, empty = "No matches.", testid = "workspace-switcher", variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const list = items.some((s) => s.id === currentId) ? items : [{ id: currentId, label: currentLabel }, ...items];
  const title = variant === "title";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {title ? (
          <button type="button" data-testid={testid} title="Switch to another record"
                  className="group flex min-w-0 max-w-full items-center gap-1.5 rounded-md text-left hover:text-[#007BFF] dark:hover:text-blue-300">
            <h1 className="truncate text-lg font-semibold leading-tight tracking-tight text-slate-900 group-hover:underline dark:text-slate-100">{currentLabel}</h1>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-current" />
          </button>
        ) : (
          <button type="button" className="flex h-8 w-72 items-center justify-between rounded-md border border-[#333] bg-[#1f1f1f] px-2.5 text-xs text-slate-100 hover:bg-[#262626]" data-testid={testid}>
            <span className="truncate">{currentLabel}</span><ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align={title ? "start" : "end"} className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Search..." className="h-8 text-xs" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">{empty}</CommandEmpty>
            {list.map((s) => (
              <CommandItem key={s.id} value={`${s.label} ${s.meta || ""} ${s.id}`} onSelect={() => { setOpen(false); if (s.id !== currentId) onPick(s.id); }}
                           className="text-xs" data-testid={`switcher-item-${s.id}`}>
                <Check className={`mr-1.5 h-3 w-3 ${s.id === currentId ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{s.label}</span>
                {s.meta && <span className="ml-auto pl-2 text-muted-foreground">{s.meta}</span>}
              </CommandItem>))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
